import { useStorageQuota } from "@hooks/use-storage-quota";
import type { SequenceKind } from "@lib/genetics";
import { guardStorageQuota } from "@lib/storage";
import { Button } from "@ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@ui/dialog";
import { ScrollArea } from "@ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@ui/table";
import { useImportStore } from "@upload/importStore";
import { cancelImport, confirmImport } from "@upload/upload";
import { useState } from "react";
import { useShallow } from "zustand/react/shallow";

export function ImportReviewDialog() {
  const analyzedImports = useImportStore(
    useShallow((state) =>
      state.imports.filter((item) => item.status === "analyzed"),
    ),
  );

  const { isOverQuota } = useStorageQuota();

  const [overrides, setOverrides] = useState<Map<string, SequenceKind>>(
    new Map(),
  );

  const open = analyzedImports.length > 0;

  function handleKindChange(importId: string, kind: SequenceKind) {
    setOverrides((prev) => new Map(prev).set(importId, kind));
  }

  async function handleImport() {
    if (await guardStorageQuota()) return;

    for (const item of analyzedImports) {
      const override = overrides.get(item.id);
      confirmImport(item.id, override);
    }
  }

  function handleCancel() {
    for (const item of analyzedImports) {
      cancelImport(item.id);
    }
  }

  return (
    <Dialog open={open}>
      <DialogContent showCloseButton={false} className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Review Import</DialogTitle>
          <DialogDescription>
            Confirm the detected sequence type before importing. You can
            override the type if auto-detection was incorrect.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-96">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Format</TableHead>
                <TableHead>Sequences</TableHead>
                <TableHead>Type</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analyzedImports
                .toSorted((a, b) => a.fileName.localeCompare(b.fileName))
                .map((item) => {
                  const analysis = item.analysis;
                  if (!analysis) return null;

                  const currentKind =
                    overrides.get(item.id) ?? analysis.sequenceKind;
                  const isAb1 = analysis.format === "ab1";

                  return (
                    <TableRow key={item.id}>
                      <TableCell className="max-w-48 truncate font-medium">
                        {item.fileName}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {analysis.format.toUpperCase()}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {analysis.sequenceCount}
                      </TableCell>
                      <TableCell>
                        {isAb1 ? (
                          <span className="text-muted-foreground">
                            Nucleotide
                          </span>
                        ) : (
                          <Select
                            value={currentKind ?? "nt"}
                            onValueChange={(value: string) =>
                              handleKindChange(item.id, value as SequenceKind)
                            }
                          >
                            <SelectTrigger size="sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="nt">Nucleotide</SelectItem>
                              <SelectItem value="aa">Amino Acid</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={isOverQuota}>
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
